import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { listPatientsPage, hl7Zuordnen, type ReviewQueueItem, type Patient } from '../api/unimedica';

/* Laborbefund ohne Zuordnung einer Akte zuweisen.
 *
 * Bewusst kein Prüf-Formular wie bei Dokumenten: hier fehlt nur EINE Angabe — welcher Patient.
 * Links stehen die Daten aus der Nachricht (so wie das Labor sie geliefert hat), rechts die
 * Suche in der Stammliste. Die Voreinstellung merkt sich die Kennung, damit derselbe Fall nicht
 * bei jeder Lieferung erneut im Eingang landet.
 */
export default function Hl7Zuordnen({ item, onClose, onFertig }:
  { item: ReviewQueueItem; onClose: () => void; onFertig: (meldung: string) => void }) {
  const r = item.recognized ?? {};
  const kennungen = (r.kennungen ?? []) as string[];
  const werte = (r.werte ?? []) as any[];
  // Ohne Namen ist die am Geraet getippte Nummer der einzige Anhaltspunkt — damit suchen.
  const [q, setQ] = useState<string>(String(r.nachname || kennungen[0] || ''));
  const [treffer, setTreffer] = useState<Patient[]>([]);
  const [suchend, setSuchend] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<Patient | null>(null);
  const [alias, setAlias] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    // Jeder Tastendruck bricht die vorige Suche ab. Ohne das liefen beim Tippen fuenf
    // Abfragen nebeneinander, und die letzte — die einzige, die zaehlt — kam zuletzt.
    const ctl = new AbortController();
    const t = setTimeout(() => {
      if (q.trim().length < 2) { setTreffer([]); return; }
      setSuchend(true);
      listPatientsPage({ alle: true, q: q.trim(), limit: 12, signal: ctl.signal })
        .then((s) => setTreffer(s.patients ?? []))
        .catch(() => { if (!ctl.signal.aborted) setTreffer([]); })
        .finally(() => { if (!ctl.signal.aborted) setSuchend(false); });
    }, 300);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [q]);

  const daten = useMemo(() => {
    const z: [string, string][] = [];
    const name = [r.vorname, r.nachname].filter(Boolean).join(' ');
    if (name) z.push(['Name laut Labor', name]);
    if (r.geburtsdatum) z.push(['Geburtsdatum', String(r.geburtsdatum)]);
    z.push(['Patientennummer', kennungen.length ? kennungen.join(', ') : 'am Gerät nicht eingegeben']);
    if (r.absender) z.push(['Absender', String(r.absender)]);
    if (r.datum) z.push(['Befunddatum', String(r.datum)]);
    z.push(['Inhalt', `${r.laborwerte ?? 0} Laborwerte${r.dokumente ? `, ${r.dokumente} Dokumente` : ''}`]);
    return z;
  }, [r, kennungen]);

  const zuordnen = async () => {
    if (!gewaehlt) return;
    setBusy(true); setFehler(null);
    try {
      const res = await hl7Zuordnen(item.ref, gewaehlt.patient_id, alias);
      onFertig(`${res.laborwerte} Laborwerte in die Akte übernommen.`);
    } catch (e: any) {
      setFehler(e?.message ?? String(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-xl bg-white shadow-xl sm:rounded-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-ps-dark-900">Laborbefund zuordnen</h2>
            <p className="text-xs text-ps-dark-400">
              {r.ohne_kennung
                ? 'Am Laborgerät wurde keine Patientennummer eingegeben. Wählen Sie die Akte, in die der Befund gehört.'
                : `Die Nummer ${kennungen.join(', ')} passt zu keiner Akte. Wählen Sie die Akte, in die der Befund gehört.`}
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Schliessen"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="grid gap-4 p-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ps-dark-500">Aus der Nachricht</div>
            <dl className="divide-y divide-gray-100 rounded border border-gray-200">
              {daten.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[9rem_minmax(0,1fr)] gap-2 px-3 py-2">
                  <dt className="text-xs text-ps-dark-400">{k}</dt>
                  <dd className="break-words text-sm text-ps-dark-800">{v}</dd>
                </div>
              ))}
            </dl>

            {werte.length > 0 && (
              <>
                <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ps-dark-500">
                  Gemessene Werte
                </div>
                {/* Ohne Namen erkennt die Praxis den Fall nur an den Werten selbst. */}
                <div className="max-h-64 overflow-y-auto rounded border border-gray-200">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {werte.map((v: any, i: number) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5 text-ps-dark-700">{v.parameter}</td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums text-ps-dark-900">
                            {v.wert}{v.einheit ? <span className="ml-1 font-normal text-ps-dark-400">{v.einheit}</span> : null}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right text-xs text-ps-dark-400">{v.referenz}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ps-dark-500">Akte wählen</div>
            <div className="relative mb-2">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-ps-dark-300" />
              <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setGewaehlt(null); }}
                placeholder="Name oder Praxis-ID" className="input w-full pl-8 text-sm" />
            </div>
            <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded border border-gray-200">
              {suchend && <div className="px-3 py-3 text-xs text-ps-dark-400">Suche …</div>}
              {!suchend && treffer.length === 0 && (
                <div className="px-3 py-3 text-xs text-ps-dark-400">
                  {q.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben.' : (
                    <>
                      Kein Treffer.
                      {r.stammliste_stand ? ` Die Patientenliste stammt vom ${r.stammliste_stand} — wer danach in siMed angelegt wurde, steht noch nicht darin.` : ''}
                    </>
                  )}
                </div>
              )}
              {treffer.map((p) => {
                const aktiv = gewaehlt?.patient_id === p.patient_id;
                return (
                  <button key={p.patient_id} onClick={() => setGewaehlt(p)}
                    className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${aktiv ? 'bg-primary-50' : ''}`}>
                    <span className="font-medium text-ps-dark-900">{p.nachname} {p.vorname}</span>
                    <span className="block text-[11px] text-ps-dark-400">
                      geb. {p.geburtsdatum || '?'} · {String(p.patient_id).split('_')[0]}
                    </span>
                  </button>
                );
              })}
            </div>
            <label className="mt-3 flex items-start gap-2 text-xs text-ps-dark-600">
              <input type="checkbox" checked={alias} onChange={(e) => setAlias(e.target.checked)} className="mt-0.5" />
              <span>Kennung merken — künftige Befunde dieses Labors gehen automatisch in diese Akte.</span>
            </label>
          </div>
        </div>

        {fehler && <div className="mx-4 mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{fehler}</div>}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
          <div className="text-xs text-ps-dark-400">
            {gewaehlt ? <>Ziel: <span className="font-medium text-ps-dark-700">{gewaehlt.nachname} {gewaehlt.vorname}</span></> : 'Noch keine Akte gewählt'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost text-sm">Abbrechen</button>
            <button onClick={() => void zuordnen()} disabled={!gewaehlt || busy} className="btn-primary text-sm">
              {busy ? 'Wird übernommen …' : 'Zuordnen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
